import axiosInstanceV2 from "@lhd/libs/axiosV2";
import { useQuery } from "@tanstack/react-query";

// ============================================================
// TypeScript Type Definitions for FIRE RPG Job Configs
// Reference: fire_rpg_job_configs_api_guide_for_fe.md
// ============================================================

export type FireRpgJobConfig = {
  id: number;
  job_name: string;
  job_field: string | null;
  description: string | null;
  single_starting_salary: string | number;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type CreateFireRpgJobConfigInput = {
  job_name: string;
  job_field?: string | null;
  description?: string | null;
  single_starting_salary: number;
  sort_order?: number;
};

export type UpdateFireRpgJobConfigInput = {
  job_name?: string;
  job_field?: string | null;
  description?: string | null;
  single_starting_salary?: number;
  sort_order?: number;
};

// ============================================================
// Helper: Format Salary in Japanese Currency format (e.g. "360 万円")
// Handles string or number representation from Rails backend.
// ============================================================

export const formatSalary = (
  val: string | number | null | undefined,
): string => {
  if (val == null || val === "") return "-";
  const num = typeof val === "number" ? val : parseFloat(val);
  if (Number.isNaN(num)) return "-";

  // Format with thousands separator
  const formatted = Number.isInteger(num)
    ? num.toLocaleString("ja-JP")
    : num.toLocaleString("ja-JP", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

  return `${formatted} 万円`;
};

// ============================================================
// API Endpoints
// Base URL is configured in axiosInstanceV2 (/api/v2)
// ============================================================

const JOB_CONFIGS_ENDPOINT = "/fire_rpg/job_configs";

/**
 * Fetch all job configs (accessible by all logged-in users).
 * Returns array sorted by sort_order ascending, then id.
 */
export const fetchJobConfigs = async (): Promise<FireRpgJobConfig[]> => {
  const response = await axiosInstanceV2<FireRpgJobConfig[]>({
    method: "get",
    url: JOB_CONFIGS_ENDPOINT,
  });
  return response.data || [];
};

export const useFireRpgJobConfigs = () =>
  useQuery(["fire_rpg", "job_configs"], fetchJobConfigs);

/**
 * Create a new job config (Super Admin only).
 * Returns created job config with id and timestamps.
 */
export const createJobConfig = async (
  payload: CreateFireRpgJobConfigInput,
): Promise<FireRpgJobConfig> => {
  const response = await axiosInstanceV2<FireRpgJobConfig>({
    method: "post",
    url: JOB_CONFIGS_ENDPOINT,
    data: payload,
  });
  return response.data;
};

/**
 * Update an existing job config (Super Admin only).
 * Performs partial update (PATCH). For optional fields, sending null clears them.
 */
export const updateJobConfig = async (
  id: number,
  payload: UpdateFireRpgJobConfigInput,
): Promise<FireRpgJobConfig> => {
  const response = await axiosInstanceV2<FireRpgJobConfig>({
    method: "patch",
    url: `${JOB_CONFIGS_ENDPOINT}/${id}`,
    data: payload,
  });
  return response.data;
};

/**
 * Delete a job config by ID (Super Admin only).
 * Returns 204 No Content on success.
 * May return 422 Unprocessable Entity if referenced by player family state.
 */
export const deleteJobConfig = async (id: number): Promise<void> => {
  await axiosInstanceV2({
    method: "delete",
    url: `${JOB_CONFIGS_ENDPOINT}/${id}`,
  });
};
